import { useState, useMemo } from 'react'
import { AccountingService } from '@repo/entity-accounting'
import type { BankTransaction } from '@repo/entity-accounting'

type CsvBankTransaction = {
  date: string
  description: string
  amount: number
  reference: string
  rawRow: string
}

export function BankImportPage() {
  const [csvText, setCsvText] = useState('')
  const [columnMap, setColumnMap] = useState({ date: 0, description: 1, amount: 2, reference: 3 })
  const [hasHeader, setHasHeader] = useState(true)
  
  // Hardcoded for demo - in reality this comes from db
  const tenantId = 't1'
  const bankAccountId = 'bank1'
  
  const rules = useMemo(() => AccountingService.buildDefaultBankRules(tenantId, bankAccountId), [tenantId, bankAccountId])
  const accounts = useMemo(() => AccountingService.getDefaultChartOfAccounts(tenantId) as any[], [tenantId])

  const [transactions, setTransactions] = useState<CsvBankTransaction[]>([])
  const [categorization, setCategorization] = useState<{ matched: BankTransaction[], unmatched: CsvBankTransaction[] } | null>(null)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      setCsvText(text)
    }
    reader.readAsText(file)
  }

  const handleParse = () => {
    if (!csvText) return
    const parsed = AccountingService.parseBankCsv(csvText, columnMap, hasHeader)
    setTransactions(parsed)
    setCategorization(null)
  }

  const handleCategorize = () => {
    const result = AccountingService.autoCategorizeBankImport(transactions, rules, accounts, tenantId, bankAccountId)
    setCategorization(result)
  }

  const handleConfirm = () => {
    alert('Import confirmed! (mock)')
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Bank CSV Import & Auto-Categorization</h1>
      
      <div className="bg-white p-6 rounded shadow-sm border border-gray-200 mb-6">
        <h2 className="text-lg font-semibold mb-4">1. Upload CSV</h2>
        <div className="border-2 border-dashed border-gray-300 rounded p-10 text-center hover:bg-gray-50 transition-colors">
          <input 
            type="file" 
            accept=".csv"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>
      </div>

      {csvText && (
        <div className="bg-white p-6 rounded shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold mb-4">2. Map Columns</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date Column</label>
              <input type="number" value={columnMap.date} onChange={e => setColumnMap(m => ({ ...m, date: parseInt(e.target.value) }))} className="w-full border rounded p-2" min={0} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description Column</label>
              <input type="number" value={columnMap.description} onChange={e => setColumnMap(m => ({ ...m, description: parseInt(e.target.value) }))} className="w-full border rounded p-2" min={0} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Amount Column</label>
              <input type="number" value={columnMap.amount} onChange={e => setColumnMap(m => ({ ...m, amount: parseInt(e.target.value) }))} className="w-full border rounded p-2" min={0} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reference Column</label>
              <input type="number" value={columnMap.reference} onChange={e => setColumnMap(m => ({ ...m, reference: parseInt(e.target.value) }))} className="w-full border rounded p-2" min={0} />
            </div>
          </div>
          <div className="mb-4">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} className="rounded text-blue-600" />
              <span className="text-sm">File has header row</span>
            </label>
          </div>
          <button onClick={handleParse} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Parse CSV
          </button>
        </div>
      )}

      {transactions.length > 0 && !categorization && (
        <div className="bg-white p-6 rounded shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold mb-4">3. Parsed Transactions ({transactions.length})</h2>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Description</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Amount (₱)</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.slice(0, 10).map((t, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2">{t.date}</td>
                    <td className="px-4 py-2">{t.description}</td>
                    <td className="px-4 py-2 text-right">₱ {t.amount.toFixed(2)}</td>
                    <td className="px-4 py-2">{t.reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {transactions.length > 10 && (
              <p className="text-sm text-gray-500 mt-2 text-center">...and {transactions.length - 10} more rows</p>
            )}

          </div>
          <button onClick={handleCategorize} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
            Auto-Categorize
          </button>
        </div>
      )}

      {categorization && (
        <div className="bg-white p-6 rounded shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold mb-4">4. Auto-Match Results</h2>
          <div className="flex space-x-4 mb-4">
            <div className="bg-green-50 text-green-700 p-3 rounded flex-1">
              <div className="font-bold text-xl">{categorization.matched.length}</div>
              <div className="text-sm">Matched</div>
            </div>
            <div className="bg-orange-50 text-orange-700 p-3 rounded flex-1">
              <div className="font-bold text-xl">{categorization.unmatched.length}</div>
              <div className="text-sm">Unmatched</div>
            </div>
          </div>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Description</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Amount (₱)</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Match</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {categorization.matched.map((m, i) => {
                  const account = accounts.find(a => a.code === m.matchedJournalLineId)
                  return (
                    <tr key={`m-${i}`} className="bg-green-50/30">
                      <td className="px-4 py-2 text-green-600">
                        <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      </td>
                      <td className="px-4 py-2">{new Date(m.transactionDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2">{m.description}</td>
                      <td className="px-4 py-2 text-right">₱ {m.amount.toFixed(2)}</td>
                      <td className="px-4 py-2 font-medium">{m.matchedJournalLineId} - {account?.name}</td>
                    </tr>
                  )
                })}
                {categorization.unmatched.map((u, i) => (
                  <tr key={`u-${i}`} className="bg-orange-50/30">
                    <td className="px-4 py-2 text-orange-500">
                      <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </td>
                    <td className="px-4 py-2">{u.date}</td>
                    <td className="px-4 py-2">{u.description}</td>
                    <td className="px-4 py-2 text-right">₱ {u.amount.toFixed(2)}</td>
                    <td className="px-4 py-2 text-gray-400 italic">Needs manual review</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <button onClick={handleConfirm} className="bg-blue-600 text-white px-6 py-2 rounded font-medium hover:bg-blue-700">
              Confirm Import
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
